'use client';

import React from 'react';

interface EnvErrorProps {
  missingVars: string[];
}

/**
 * User-friendly error UI rendered in the browser when critical
 * NEXT_PUBLIC_ environment variables are missing, instead of a cryptic crash.
 */
export default function EnvError({ missingVars }: EnvErrorProps) {
  if (missingVars.length === 0) return null;

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-gray-900 border border-red-500/40 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-red-600/20 border-b border-red-500/30 px-6 py-4 flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-red-300 font-semibold text-base leading-tight">
              Configuration Error
            </h1>
            <p className="text-red-400/70 text-xs mt-0.5">
              PREP Platform cannot start
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-gray-300 text-sm mb-4">
            The following required environment variable
            {missingVars.length > 1 ? 's are' : ' is'} missing. The application
            cannot connect to its backend without them.
          </p>

          <ul className="space-y-2 mb-5">
            {missingVars.map((key) => (
              <li
                key={key}
                className="flex items-center gap-2 bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-2"
              >
                <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                <code className="text-red-300 text-xs font-mono">{key}</code>
              </li>
            ))}
          </ul>

          <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg px-4 py-3">
            <p className="text-blue-300 text-xs font-medium mb-1">How to fix</p>
            <ol className="text-blue-200/70 text-xs space-y-1 list-decimal list-inside">
              <li>Open your <code className="bg-gray-800 px-1 rounded">.env</code> file in the project root.</li>
              <li>Add the missing variables with their correct values.</li>
              <li>Restart the development server.</li>
            </ol>
            <p className="text-blue-200/50 text-xs mt-2">
              Supabase keys are found in{' '}
              <span className="text-blue-300">
                Dashboard → Project Settings → API
              </span>
              .
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-3">
          <p className="text-gray-600 text-xs text-center">
            PREP Platform · Environment Configuration Required
          </p>
        </div>
      </div>
    </div>
  );
}
